import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import React, { FC, useCallback, useContext } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgClubTrendhim from '@Assets/SVG/ClubTrendhim'
import SvgEdit from '@Assets/SVG/Edit'
import { NativeModalBody, NativeModalFooter, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { TRUST_PILOT_WIDGET_TEMPLATE_MICRO_STAR, TrustPilotWidget } from '@Components/TrustPilotWidget'
import { Button } from '@CoreComponents/Buttons/Button'
import { CartV2LineItems, CartV2Totals, CartV2Type } from '@Domains/Cart/Feature/CartV2/CartV2'
import { LineItem } from '@Domains/Cart/Feature/CartV2/Components/CartOverview/LineItem/LineItem'
import { DeliveryCartField } from '@Domains/Cart/Feature/CartV2/Components/CartTotals/FieldListing/Fields/DeliveryCartField/DeliveryCartField'
import { SideCartEmpty } from '@Domains/Cart/Feature/CartV2/Components/SideCartEmpty/SideCartEmpty'
import { MetadataContext } from '@Globals/Metadata'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './OrderSummaryV3.module.scss'

interface OrderSummaryV2SidebarProps {
  expectedDelivery?: string
  isFinalized?: boolean
  lineItemGroups?: CartV2Type['lineItemGroups']
  lineItems: CartV2LineItems
  onClose?: () => void
  showEdit?: boolean
  totals: CartV2Totals
}

export const OrderSummaryV3: FC<OrderSummaryV2SidebarProps> = ({
  expectedDelivery,
  isFinalized = false,
  lineItemGroups,
  lineItems,
  onClose,
  showEdit = true,
  totals,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const { storeURL } = useContext(MetadataContext)

  const getFreeGiftParent = useCallback(lineItem => lineItems.find(
    l => l.type && lineItem.type && l.type.__typename === 'CartLineItemTypeFreeProduct' && 'parent' in lineItem.type && l.type.link === lineItem.type.parent,
  ), [lineItems])

  if (isEmpty(lineItems)) {
    return (
      <div className={styles.orderSummary}>
        <SideCartEmpty />
      </div>
    )
  }

  return (
    <>
      <NativeModalHeader className={clsx(cofDesignV1 && styles.modalHeader)}>
        {t('common:COF_ORDER_SUMMARY')}
      </NativeModalHeader>
      <NativeModalBody className={clsx(cofDesignV1 && styles.modalBody)}>
        <div className={clsx(styles.orderSummary, cofDesignV1 && styles.cofDesignV1)}>
          {showEdit && (
            <a
              aria-label={t('checkout:EDIT_CART_LABEL')}
              className={clsx(styles.editCart, cofDesignV1 && styles.cofDesignV1)}
              href={storeURL('')}
              onClick={() => {
                ampli.navLinkClick({
                  dest_page_type: 'cart',
                  dest_page_type_id: '1',
                  source_id: 'cart_summary_edit_cart',
                })
              }}
            >
              <SvgEdit />
              {t('checkout:EDIT_CART_LABEL')}
            </a>
          )}
          <div className={styles.lineitems}>
            {map(lineItems, lineItem => (
              lineItemGroups?.find(group => group.lineItemIDs?.includes(lineItem.id))
                ? null
                : (
                  <LineItem
                    key={lineItem.id}
                    isFinalized={isFinalized}
                    isPartOfBundle={!!(lineItem.type && 'parent' in lineItem.type && !isEmpty(lineItem.type.parent))}
                    lineItem={lineItem}
                    linkToProduct={false}
                    parent={getFreeGiftParent(lineItem)}
                    personalizationsLinkedToLineItem={lineItems.filter(
                      l => l.type?.__typename === 'CartLineItemTypeUpsellPersonalization' && l.type?.parent === lineItem.type?.link,
                    )}
                    showPersonalizationModifiers
                    showPersonalizeImagesOnAddedProducts={false}
                    showPersonalizeImagesOnUnaddedProducts={false}
                    showQuantityBubble
                    showQuantityButtons={false}
                    showUpsell={false}
                  />
                )
            ))}
            {map(lineItemGroups, group => (
              <div key={`lineitemgroup_${group.id}`} className={clsx(styles.lineItemGroupWrapper)}>
                <div className={styles.groupHeader}>
                  <p>{t('cart:BUNDLE_DISCOUNT_LABEL')}</p>
                  <span className={styles.prices}>
                    {group.totalPrice?.discount?.format && <p className={clsx(styles.discount)}>{group.totalPrice?.discount?.format}</p>}
                    <p className={clsx(styles.price, group.totalPrice?.discount?.format && styles.discounted)}>{group.totalPrice?.format}</p>
                  </span>
                </div>
                <div className={styles.lineItemGroup}>
                  {map(lineItems?.filter(lineitem => group.lineItemIDs?.includes(lineitem.id)), lineItem => (
                    <LineItem
                      key={`lineitemgroup_item_${lineItem.id}`}
                      className={styles.lineItemInGroup}
                      groupItem
                      isPartOfBundle={!!(lineItem.type && 'parent' in lineItem.type && !isEmpty(lineItem.type.parent))}
                      lineItem={lineItem}
                      parent={getFreeGiftParent(lineItem)}
                      personalizationsLinkedToLineItem={lineItems?.filter(
                        l => l.type?.__typename === 'CartLineItemTypeUpsellPersonalization' && l.type?.parent === lineItem.type?.link,
                      )}
                      showPersonalizationModifiers={false}
                      showPersonalizeImagesOnAddedProducts={false}
                      showPersonalizeImagesOnUnaddedProducts={false}
                      showQuantity={false}
                      showQuantityBubble={false}
                      showQuantityButtons={false}
                      showUpsell={false}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
          {cofDesignV1 && (
            <div className={clsx(styles.orderSummaryBodyFooter, 'fullwidth')}>
              {expectedDelivery && (
                <DeliveryCartField
                  className={clsx(!cofDesignV1 && styles.expectedDelivery)}
                  expectedDelivery={expectedDelivery || ''}
                  hideButton={isFinalized || !cofDesignV1}
                  v2={cofDesignV1}
                />
              )}

              {!isEmpty(totals) && (
                <div className={styles.totals}>
                  {totals?.map(total => (
                    <div key={total.id} className={styles.total}>
                      {total.id === 'cashback'
                        ? (
                          <>
                            <span>
                              <SvgClubTrendhim className={styles.cashbackIcon} />
                            </span>
                            <span>
                              {total.value?.format ? total.value.format : total.text}
                            </span>
                          </>
                        )
                        : (
                          <>
                            <span>
                              {total.title}
                            </span>
                            <span>
                              {total.value?.format ? total.value.format : total.text}
                            </span>
                          </>
                        )}
                    </div>
                  ))}
                </div>
              )}
              <TrustPilotWidget className={styles.trust} link={false} trustPilotWidgetTemplate={TRUST_PILOT_WIDGET_TEMPLATE_MICRO_STAR} />
            </div>
          )}

        </div>
      </NativeModalBody>
      {cofDesignV1
        ? (
          <NativeModalFooter className={clsx(styles.modalFooter, cofDesignV1 && styles.cofDesignV1)}>
            {onClose && (
              <Button className={styles.button} fillWidth innerSpacing="medium" onClick={onClose} theme="black">
                {t('CLOSE')}
              </Button>
            )}
          </NativeModalFooter>
        )
        : (!isEmpty(totals) || expectedDelivery) && (
          <NativeModalFooter className={styles.modalFooter}>
            {expectedDelivery && (
              <DeliveryCartField
                className={styles.expectedDelivery}
                expectedDelivery={expectedDelivery || ''}
                hideButton
              />
            )}

            {totals?.map(total => (
              <div key={total.id} className={clsx(styles.total, styles[total.style.toLowerCase()])}>
                <span>{total.title}</span>
                <span className={styles.totalAmount}>
                  {total.value?.format ? total.value.format : total.text}
                </span>
              </div>
            ))}
          </NativeModalFooter>
        )}
    </>
  )
}

